import React, { useState } from 'react';
import Hero from '../../../components/Hero/Hero';
import Pathfinder from '../../../components/Pathfinder/Pathfinder';
import Metadata from '../../../components/Metadata/Metadata';
import Section from '../../../components/Section/Section';
import Card from '../../../components/Card/Card';
import GlobalCallout from '../../../components/Global/CalloutDefault';
import { Box, Typography } from '@mui/material';
import Wallpaper from '../../../components/Wallpaper/Wallpaper';
import Accordion from '../../../components/Accordion/Accordion';
import Button from '../../../components/Button/Button';
import Image from '../../../components/Image/Image';
import ContentSplit from '../../../components/ContentSplit/ContentSplit';
import ListItem from '../../../components/ListItem/ListItem';

const PageContent: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };

  return (
    <Box>
      <Metadata
        title="AI Solutions for Sports Medicine Innovation"
        description="Revolutionize sports medicine with AI-driven insights. Optimize performance, recovery, and injury prevention with General Genomics."
      />
      <Hero
        helper="Industries &gt; Commercial"
        headline="AI Solutions for Sports Medicine Innovation"
      />

      <Section theme="" className="cards-large-icons">
        <h2 className="action-on-view action-fade-in">
          Transforming Sports Medicine with AI
        </h2>
        <p className="action-on-view action-fade-in">
          General Genomics empowers sports medicine with advanced AI solutions,
          enhancing performance, injury prevention, and recovery. Our
          data-driven insights optimize athlete care, driving better outcomes in
          sports health and wellness.
        </p>
        <Image
          path="/images/library/sports-football.webp"
          size="wide"
          className="action-on-view action-fade-in"
          alt="This is a wide image."
        />
      </Section>

      <ContentSplit image="/images/library/sports-hockey.webp" position="right">
        <p className="helper action-on-view action-fade-in">
          Performance Optimization
        </p>
        <h2 className="action-on-view action-fade-in">
          Enhance Athlete Performance and Health
        </h2>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Injury Prediction:</strong> Predict injury risks with
            advanced machine learning models.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Personalized Training:</strong> Personalize training and
            recovery plans using genetic data.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Health Monitoring:</strong> Monitor athlete health with
            real-time analytics.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Team Optimization:</strong> Optimize team performance with
            data-driven decision support.
          </p>
        </ListItem>
      </ContentSplit>

      <Section theme="dark" className="section-cross-linking">
        <h2 className="action-on-view action-fade-in">
          Explore More Solutions
        </h2>
        <p className="action-on-view action-fade-in">
          Discover how General Genomics can revolutionize sports medicine with
          data-driven insights and personalized healthcare solutions.
        </p>
        <div className="card-group">
          <Card
            icon="list_alt"
            headline="Our Capabilities"
            subhead="Learn about our full suite of data solutions, including AI-driven analytics and predictive modeling, tailored to enhance athletic performance and health outcomes."
            btnURL="/our-approach/our-capabilities"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />

          <Card
            icon="construction"
            headline="Innovation Research & Development"
            subhead="See how our commitment to R&D drives cutting-edge solutions that transform injury prevention, recovery, and athlete optimization."
            btnURL="/our-approach/innovation-research-and-development"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />

          <Card
            icon="vaccines"
            headline="Pharmaceuticals"
            subhead="Discover how our expertise in precision medicine and genomic research can advance treatments and recovery solutions in sports medicine."
            btnURL="/industries/commercial/pharmaceuticals"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
        </div>
      </Section>

      <Wallpaper asset="../../images/library/sports-racing.webp"></Wallpaper>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently asked questions about General Genomics&apos; and Sports
          Medicine
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Discover how General Genomics revolutionizes Sports Medicine with
          AI-driven insights to optimize performance, recovery, and injury
          prevention.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline="How can General Genomics improve athlete performance?"
          >
            Our AI models analyze performance metrics and genetic data to create
            personalized training and recovery plans, maximizing athletic
            potential.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="Can AI help in injury prevention?"
          >
            Yes, we predict injury risks using machine learning, enabling
            proactive interventions and reducing recovery time.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How does General Genomics personalize training?"
          >
            We use genetic markers and performance data to tailor training
            regimens, enhancing efficiency and safety.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="What insights can be gained from genomic research?"
          >
            Our genomic analytics identify optimal nutrition, recovery, and
            performance strategies tailored to each athlete’s biology.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How secure is the data used in sports medicine solutions?"
          >
            We prioritize data security with state-of-the-art encryption and
            compliance with privacy regulations.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Why partnering with General Genomics revolutionizes sports medicine
          and athlete care
        </Typography>
        <Box className="card-group">
          <Card
            icon="check"
            headline="Data-Driven Insights"
            subhead="Enhance decision-making with predictive analytics and real-time performance monitoring."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="check"
            headline="Personalized Healthcare"
            subhead="Tailor training, recovery, and nutrition using genetic and performance data."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="check"
            headline="Proactive Injury Prevention"
            subhead="Leverage AI to predict and prevent injuries, optimizing athlete health and longevity."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <GlobalCallout />

      <Pathfinder />
    </Box>
  );
};

export default PageContent;
